import {
  getNewDocumentCTAVisibilityOnOverlayClose,
  getNewDocumentCTAVisibilityOnOverlayOpen,
} from "./getNewDocumentCTAVisibility";

interface OverlayState {
  showCommandMenu: boolean;
  commandModeState: string;
  commandModeInserting: boolean;
}

/**
 * Computes the new-document CTA visibility updates that should occur
 * after an editor state transition. Returns a boolean value to apply
 * to setShowNewDocumentCTAs, or null if no CTA update is needed for
 * this transition.
 *
 * This function is the single source of truth for Quick Edit
 * lifecycle CTA transitions. ProseGenerator uses getDraftCTAUpdate
 * because it observes a different state shape (proseGeneratorActive boolean).
 */
export function getQuickEditCTAUpdates(
  prevState: OverlayState,
  currentState: OverlayState,
  getText: () => string | undefined | null
): boolean | null {
  // Quick Edit opened: hide CTAs
  if (currentState.showCommandMenu) {
    return getNewDocumentCTAVisibilityOnOverlayOpen();
  }
  // Quick Edit closed: restore based on content
  if (prevState.showCommandMenu && !currentState.showCommandMenu) {
    return getNewDocumentCTAVisibilityOnOverlayClose(getText);
  }
  return null;
}

export function getCommanderCTAUpdate(
  prevState: OverlayState,
  currentState: OverlayState,
  getText: () => string | undefined | null
): boolean | null {
  // Commander closed (unmounts, so parent handles this)
  if (prevState.commandModeState !== "off" && currentState.commandModeState === "off") {
    // When Commander transitions to off because it's inserting text,
    // keep CTAs hidden — text is about to arrive but getText() is still empty
    if (currentState.commandModeInserting) {
      return false;
    }
    return getNewDocumentCTAVisibilityOnOverlayClose(getText);
  }
  return null;
}

export function getDraftCTAUpdate(
  proseGeneratorActive: boolean,
  getText: () => string | undefined | null
): boolean {
  if (proseGeneratorActive) {
    return getNewDocumentCTAVisibilityOnOverlayOpen();
  }
  return getNewDocumentCTAVisibilityOnOverlayClose(getText);
}
